import {
  FIELD_TYPES,
  ACTIONS,
  CHARTS,
  RESULT_TYPES,
} from "@/utils/calculatorSchema";

import {
  calculateLoanSummary,
  generateAmortizationSchedule,
  generateMonthlySchedule,
  getLoanBreakdownData,
  getOutstandingBalanceData,
  getPrincipalInterestTrend,
  formatAxisCurrency,
} from "@/utils/finance";

import { Wallet, Percent, Landmark, Calculator } from "lucide-react";

import DataTable from "@/components/calculator/DataTable";

const emiConfig = {
  id: "emi",

  shortTitle: "EMI",

  title: "Home Loan EMI Calculator",

  subtitle:
    "Calculate your monthly EMI, total interest payable, loan repayment schedule and outstanding balance.",

  icon: Calculator,

  description:
    "Calculate your monthly home loan EMI, total interest payable, total repayment amount and view complete amortization schedules with interactive charts.",

  keywords: [
    "EMI",
    "Home Loan",
    "Mortgage",
    "Interest",
    "Amortization",
    "Loan Calculator",
  ],

  fields: [
    {
      key: "principal",
      label: "Loan Amount",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 5000000,
      min: 100000,
      max: 200000000,
      step: 100000,

      presets: [
        { label: "₹25L", value: 2500000 },
        { label: "₹50L", value: 5000000 },
        { label: "₹75L", value: 7500000 },
        { label: "₹1Cr", value: 10000000 },
        { label: "₹2Cr", value: 20000000 },
        { label: "₹5Cr", value: 50000000 },
        { label: "₹10Cr", value: 100000000 },
        { label: "₹20Cr", value: 200000000 },
      ],
    },

    {
      key: "annualRate",
      label: "Interest Rate",

      type: FIELD_TYPES.PERCENT,

      defaultValue: 8.5,

      min: 1,

      max: 20,

      step: 0.05,
    },

    {
      key: "tenureYears",

      label: "Loan Tenure",

      type: FIELD_TYPES.NUMBER,

      defaultValue: 20,

      min: 1,

      max: 40,

      step: 1,
    },
  ],

  summaryCards: [
    {
      key: "emi",
      title: "Monthly EMI",
      description: "Monthly repayment amount",
      type: RESULT_TYPES.CURRENCY,
      icon: Wallet,
    },
    {
      key: "totalInterest",
      title: "Total Interest",
      description: "Total interest payable over the loan tenure",
      type: RESULT_TYPES.CURRENCY,
      icon: Percent,
    },
    {
      key: "totalPayment",
      title: "Total Payment",
      description: "Principal plus total interest",
      type: RESULT_TYPES.CURRENCY,
      icon: Landmark,
    },
  ],

  charts: [
    {
      id: "loan-breakdown",

      type: CHARTS.PIE,

      title: "Loan Breakdown",

      resultsKey: "loanBreakdown",

      dataKey: "value",

      nameKey: "name",

      innerRadius: 70,

      outerRadius: 110,

      minWidth: 580,
    },

    {
      id: "outstanding-balance",

      type: CHARTS.LINE,

      title: "Outstanding Balance",

      resultsKey: "outstandingBalance",

      minWidth: 580,

      xAxis: {
        dataKey: "year",
      },

      yAxis: {
        tickFormatter: formatAxisCurrency,
      },

      lines: [
        {
          dataKey: "balance",
        },
      ],
    },

    {
      id: "principal-interest",

      type: CHARTS.AREA,

      title: "Principal vs Interest",

      resultsKey: "principalInterestTrend",

      minWidth: 580,

      xAxis: {
        dataKey: "year",
      },

      yAxis: {
        tickFormatter: formatAxisCurrency,
      },

      areas: [
        {
          dataKey: "principal",
          stackId: "1",
        },
        {
          dataKey: "interest",
          stackId: "1",
        },
      ],
    },
  ],

  tables: [
    {
      title: "Yearly Schedule",

      component: DataTable,

      dataKey: "amortization",

      columns: [
        {
          key: "year",
          label: "Year",
          type: "number",
          sortable: true,
        },
        {
          key: "emi",
          label: "EMI Paid",
          type: "currency",
          sortable: true,
        },
        {
          key: "principal",
          label: "Principal",
          type: "currency",
          sortable: true,
        },
        {
          key: "interest",
          label: "Interest",
          type: "currency",
          sortable: true,
        },
        {
          key: "balance",
          label: "Outstanding",
          type: "currency",
          sortable: true,
        },
      ],
    },

    {
      title: "Monthly Schedule",

      component: DataTable,

      dataKey: "monthlySchedule",

      columns: [
        {
          key: "month",
          label: "Month",
          type: "number",
          sortable: true,
        },
        {
          key: "emi",
          label: "EMI",
          type: "currency",
          sortable: true,
        },
        {
          key: "principal",
          label: "Principal",
          type: "currency",
          sortable: true,
        },
        {
          key: "interest",
          label: "Interest",
          type: "currency",
          sortable: true,
        },
        {
          key: "balance",
          label: "Balance",
          type: "currency",
          sortable: true,
        },
      ],
    },
  ],

  actions: [ACTIONS.RESET, ACTIONS.COPY, ACTIONS.EXCEL],

  calculate(values) {
    const { principal, annualRate, tenureYears } = values;

    const options = {
      principal,
      annualRate,
      tenureYears,
    };

    const summary = calculateLoanSummary(options);

    const monthlySchedule = generateMonthlySchedule(options);

    const amortization = generateAmortizationSchedule(options);

    return {
      summary,

      charts: {
        loanBreakdown: getLoanBreakdownData(summary),

        outstandingBalance: getOutstandingBalanceData(amortization),

        principalInterestTrend: getPrincipalInterestTrend(amortization),
      },

      tables: {
        amortization,
        monthlySchedule,
      },

      metadata: {
        generatedAt: new Date(),

        ...options,
      },
    };
  },

  exportExcel: ({ results }) => ({
    "Yearly Schedule": results.tables.amortization ?? [],

    "Monthly Schedule": results.tables.monthlySchedule ?? [],
  }),
};

export default emiConfig;
